import React, {
  createContext,
  ReactNode,
  useMemo,
  useCallback,
  useContext,
  useReducer,
} from 'react'
import { Document } from '@contentful/rich-text-types'
import { useTranslations } from 'next-intl'
import { isClient } from '~/utils/envChecks'
import {
  Notification,
  NotificationAction,
  notificationReducer,
  NotificationContextType,
} from '~/context/notification'
import useErrorMessages from '~/hooks/useMessages/useErrorMessages'

export const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, dispatch] = useReducer(notificationReducer, [])

  const t = useTranslations('common.actions')
  const removeNotification = useCallback((id: string) => dispatch({ type: 'REMOVE', id }), [])
  const { getStatusCodeMessage, getErrorCodeMessage } = useErrorMessages()

  const addNotification = useCallback(
    (
      notificationId: string | undefined,
      message: string,
      options: {
        type: Notification['type']
        action?: NotificationAction
        richText?: Document
      }
    ) => {
      const refreshPage = t('refresh_page')
      const id = notificationId ?? crypto.randomUUID()

      const newNotification: Notification = {
        id,
        message,
        type: options.type,
        richText: options.richText,
        onButtonClick:
          options.action === 'RELOAD' ? () => isClient() && window.location.reload() : undefined,
        buttonLabel: options.action === 'RELOAD' ? refreshPage : undefined,
      }
      dispatch({ type: 'ADD', payload: newNotification })

      return id
    },
    [t]
  )

  const addErrorCodeNotification = useCallback(
    (
      notificationId: string | undefined,
      options: {
        type: Notification['type']
        errorCode?: string
        statusCode?: number
        action?: NotificationAction
        richText?: Document
      }
    ) => {
      const id =
        notificationId ?? `${options.statusCode ?? options.errorCode}-${crypto.randomUUID()}`
      const statusCodeMessage = getStatusCodeMessage(options.statusCode)
      const errorCodeMessage = getErrorCodeMessage(options.errorCode)

      const refreshPage = t('refresh_page')
      const newNotification: Notification = {
        id,
        message: options.statusCode ? statusCodeMessage : errorCodeMessage,
        type: options.type,
        richText: options.richText,
        onButtonClick:
          options.action === 'RELOAD' ? () => isClient() && window.location.reload() : undefined,
        buttonLabel: options.action === 'RELOAD' ? refreshPage : undefined,
      }
      dispatch({ type: 'ADD', payload: newNotification })

      return id
    },
    [getStatusCodeMessage, getErrorCodeMessage, t]
  )

  const contextValue = useMemo(
    () => ({ addNotification, addErrorCodeNotification, removeNotification, notifications }),
    [addNotification, addErrorCodeNotification, removeNotification, notifications]
  )
  return (
    <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>
  )
}

export const useNotification = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotification must be used inside NotificationProvider')
  }
  return context
}
